/**
 * `/locations/canada/<provinceUrlLabel>/<locationSlug>/`, for example
 * `/locations/canada/ontario/<slug>/`: one Canadian location's page, rendered by
 * `components/Locations/IndividualLocationContainer.tsx`. Ryerson build only.
 *
 * The Canadian counterpart of
 * `src/pages/locations/united-states/[stateUrlLabel]/[locationSlug].tsx` and the same apart from
 * the brand check and the `Country.ca` list: pages exist for locations
 * whose Contentful `state` equals a header province's English `label` (provinces are stored in
 * the `state` field), and the details are looked up by slug alone. The route tree is in
 * `src/pages/locations/index.tsx`. Pinned by
 * `src/unit-test/pages/locations/canada/provinceUrlLabel/locationSlug.spec.tsx`.
 */
import IndividualLocationContainer from "@components/Locations/IndividualLocationContainer";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import { getContentfulLocationDetailsByLocationSlug } from "@contentful/location/getContentfulLocationDetailsByLocationSlug";
import { getContentfulLocationSlugsByStateOrProvince } from "@contentful/location/getContentfulLocationSlugsByStateOrProvince";
import { ProcessedContentfulLocationAllFieldsByLanguage } from "@contentful/location/types";
import { Language } from "@ryerson/frontend.application";
import { Country } from "@ryerson/frontend.common";
import {
	allContentfulHeaderCalls,
	getFlattenedLocationsHeaderData,
	HeaderPageProps,
} from "@ryerson/frontend.contentful";
import { GetStaticPathsResult, GetStaticProps } from "next";
import React from "react";

/**
 * One path per (header province `urlLabel`, location slug) pair; no paths, and no Contentful
 * call, on any brand but Ryerson.
 */
export const getStaticPaths = async () => {
	// skip page generation
	if (process.env.NEXT_PUBLIC_COMPANY !== "ryerson") {
		// If company is not ryerson, in local dev (yarn start) next.js will still try to serve the page for any URL but since getStaticProps will not run, the page will break.
		// In production (static export), unknown paths return 404.html — so this is a local dev-only quirk.
		return { paths: [], fallback: false };
	}

	const {
		countries: { [Country.ca]: caProvinces },
	} = await getFlattenedLocationsHeaderData();

	const paths: GetStaticPathsResult["paths"] = [];

	// forEach will not wait for the async function to finish
	for (const provinceObj of caProvinces) {
		const locationSlugsByState = await getContentfulLocationSlugsByStateOrProvince(
			provinceObj.label[Language.en]
		);

		for (const locationSlug of locationSlugsByState) {
			paths.push({
				params: {
					provinceUrlLabel: provinceObj.urlLabel,
					locationSlug: locationSlug,
				},
			});
		}
	}

	return {
		paths: paths,
		fallback: false, // returns 404 for all other paths
	};
};

type PageParams = {
	provinceUrlLabel: string;
	locationSlug: string;
};

type PageProps = {
	contentfulLocationByLanguage: ProcessedContentfulLocationAllFieldsByLanguage;
};

/**
 * Build time: the header data and the location in all three languages. The lookup throws when the
 * slug has no entry in one of the languages, which fails the build.
 */
export const getStaticProps: GetStaticProps<PageProps, PageParams> = async ({ params }) => {
	const { props: headerFooterProps } = await allContentfulHeaderCalls();

	const contentfulLocationByLanguage = await getContentfulLocationDetailsByLocationSlug(
		params?.locationSlug as string
	); // casting as this param will always be defined

	return {
		props: {
			...headerFooterProps,
			contentfulLocationByLanguage,
		},
	};
};

/** A Canadian location's page; see the file header. */
const Page: React.FC<HeaderPageProps & PageProps> = (props) => {
	return (
		<HeaderLayout {...getHeaderData(props)}>
			<IndividualLocationContainer
				contentfulLocationByLanguage={props.contentfulLocationByLanguage}
			/>
		</HeaderLayout>
	);
};

export default Page;
